import type { PositionSource, TimezoneSource } from '@repo/frontend-types';

/**
 * What the wizard calls each recorded source (epic twin-details-page, feature create-rules). Keyed by
 * the union, so a value the middleware adds fails to compile here until it has a label.
 *
 * Position on map reads the position labels now; the timezone labels are for `address` and
 * `summary`, which show a timezone's source. They live here because the vocabulary is decided once.
 */
export const POSITION_SOURCE_LABEL: Record<PositionSource, string> = {
  address: 'From address',
  entered: 'Entered',
  edit_mode: 'Set in Edit mode',
};

export const TIMEZONE_SOURCE_LABEL: Record<TimezoneSource, string> = {
  country: 'From country',
  position: 'From position',
  entered: 'Entered',
  edit_mode: 'Set in Edit mode',
};

/**
 * The label for a stored position source, or `undefined` for none: `null` is "not recorded" (a twin
 * positioned before sources were recorded, or one whose position changed without naming its source),
 * and an unknown value means a newer database than this client.
 * Neither is an error, so neither draws a tag or logs anything.
 */
export const positionSourceLabel = (source: unknown): string | undefined =>
  typeof source === 'string' && Object.prototype.hasOwnProperty.call(POSITION_SOURCE_LABEL, source)
    ? POSITION_SOURCE_LABEL[source as PositionSource]
    : undefined;

/** A position's tag: `mint` says where it came from; `warn` flags the legacy placeholder. */
export interface PositionSourceTag {
  tone: 'mint' | 'warn';
  text: string;
}

/**
 * The tag a position carries, read by Position on map and Twin details' position card alike
 * (`twin-details-page-address`, R5.1): the old 51.4779, 0 placeholder is flagged *Old placeholder*
 * whoever says they put it there, and otherwise the recorded source's label, or no tag when none
 * is recorded.
 */
export const positionSourceTag = (
  source: unknown,
  placeholder: boolean,
): PositionSourceTag | undefined => {
  if (placeholder) return { tone: 'warn', text: 'Old placeholder' };
  const label = positionSourceLabel(source);
  return label ? { tone: 'mint', text: label } : undefined;
};
